import React, { useEffect, useRef, useState } from 'react';

import canUse from '../../utils/canUse';
import { setTransform } from '../../utils/style';

const willPreventDefault = canUse('passiveListener')
  ? { passive: false }
  : false;

type IStateStatus = 'pending' | 'pull' | 'active' | 'loading';

export interface IState {
  status: IStateStatus;
  dropped: boolean;
  disabled: boolean;
}
export type setDisabledFunc = (disabled: boolean) => void;
export type updateHandlerFunc = (handler: AnyAsyncFunction) => void;

const DEFAULT_LOADING_DISTANCE = 40;
const DEFAULT_DISTANCE_RATIO = 2;

export const usePullToRefresh = ({
  ref,
  disabled,
  handler,
  loadingDistance = DEFAULT_LOADING_DISTANCE,
  distanceRatio = DEFAULT_DISTANCE_RATIO,
}: {
  ref: React.RefObject<HTMLDivElement>;
  disabled: boolean;
  handler: AnyAsyncFunction;
  loadingDistance?: number;
  distanceRatio?: number;
}): [IState, setDisabledFunc, updateHandlerFunc] => {
  const [status, setStatus] = useState<IStateStatus>('pending');
  const [dropped, setDropped] = useState(false);
  const [_disabled, setDisabled] = useState(disabled);

  const cache = useRef<{
    startY: number;
    handler: AnyAsyncFunction;
  }>({
    startY: 0,
    handler,
  });

  const updateHandler: updateHandlerFunc = (handler) => {
    cache.current.handler = handler;
  };

  useEffect(() => {
    if (!ref.current) return;
    if (_disabled) return;

    const setContentStyle = (y: number) => {
      if (ref.current) {
        setTransform(ref.current, `translate3d(0px,${y}px,0px)`);
      }
    };
    const reset = () => {
      setStatus('pending');
      setContentStyle(0);
    };

    const touchStart = (e: TouchEvent) => {
      if (status === 'loading') {
        return;
      }
      cache.current.startY = e.touches[0].clientY;
      setStatus('pull');
      setDropped(false);
    };

    const touchMove = (e: TouchEvent) => {
      const currentY = e.touches[0].clientY;

      if (currentY < cache.current.startY || status === 'loading') {
        return;
      }

      const dist = (currentY - cache.current.startY) / distanceRatio;

      if (dist > 0) {
        if (e.cancelable) {
          e.preventDefault();
        }
        e.stopPropagation();

        setContentStyle(dist);

        setStatus(dist >= loadingDistance ? 'active' : 'pull');
      }
    };

    const touchEnd = async () => {
      if (status === 'active') {
        setStatus('loading');
        setDropped(true);
        setContentStyle(loadingDistance);
        try {
          await cache.current.handler();
        } catch (e) {}
      }
      reset();
    };

    ref.current.addEventListener('touchstart', touchStart);
    ref.current.addEventListener('touchmove', touchMove, willPreventDefault);
    ref.current.addEventListener('touchend', touchEnd);
    ref.current.addEventListener('touchcancel', touchEnd);
    return () => {
      if (ref.current) {
        ref.current.removeEventListener('touchstart', touchStart);
        ref.current.removeEventListener('touchmove', touchMove);
        ref.current.removeEventListener('touchend', touchEnd);
        ref.current.removeEventListener('touchcancel', touchEnd);
      }
    };
  }, [ref, disabled, handler, status, dropped, _disabled]);

  return [{ status, dropped, disabled: _disabled }, setDisabled, updateHandler];
};
